import { Fragment, useState } from 'react'

import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react'
import {
  HrefLink,
  IconMoon,
  IconProfile,
  IconSignOut,
  IconSun,
  IconXmarkLight,
  LinkButton,
  Radio,
  RadioGroup,
} from '@swaggerhub-portal/component-library'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { useFetcher } from 'react-router'
import { z } from 'zod'

import { MODE } from '../DarkMode/constants'
import { useDarkMode } from '../DarkMode/hook'
import Form from '~/components/Form'
import { useEnvironment } from '~/hooks/useEnvironment'
import darkMode from '~/images/modes/dark-mode.svg'
import lightMode from '~/images/modes/light-mode.svg'
import systemMode from '~/images/modes/system-mode.svg'
import { Environment } from '~/types/app'
import { UserInfo } from '~/types/user'

interface ProfileMobileProps {
  title: string
  isOpen: boolean
  close: () => void
  user?: UserInfo,
  accentColor?: string,
  fontFamily?: string,
}

export const ProfileMobile = ({ title, user, isOpen, close, accentColor, fontFamily }: ProfileMobileProps) => {
  const { t } = useTranslation()

  const fetcher = useFetcher()

  const [isVisible, setIsVisible] = useState(isOpen)

  const { setMode, isDark, isSystem } = useDarkMode()

  const toggleDarkMode = (mode: string) => setMode(mode as MODE)

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(() => close(), 400)
  }

  const env = useEnvironment()

  const showManageAccountLink = env !== Environment.ON_PREM || (env === Environment.ON_PREM && user?.authType?.toLowerCase() === 'internal')

  return (
    <Transition appear show={isVisible} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-neutral-80/50 dark:bg-neutral-60/50" />
        </TransitionChild>
        <div className="fixed inset-y-0 right-0 flex max-w-full">
          <TransitionChild
            as={Fragment}
            enter="transition-transform ease-in-out duration-400"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transition-transform ease-in-out duration-400"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <DialogPanel className="w-screen max-w-[400px] bg-neutral-00 dark:bg-neutral-98">
              <div className="flex items-center justify-between p-6 border-b border-neutral-30 dark:border-neutral-80">
                <DialogTitle className="font-medium text-neutral-98 text-h3 dark:text-neutral-20">
                  {title}
                </DialogTitle>
                <button onClick={handleClose} data-testid="close-button" className="p-[0.375rem] text-neutral-70 dark:text-neutral-40">
                  <IconXmarkLight height={20} width={20} />
                </button>
              </div>
              {user?.email && <div className="flex items-center w-full p-6 border-b border-neutral-30 dark:border-neutral-80">
                <div className="flex-0 text-neutral-90 dark:text-neutral-40">
                  <IconProfile height={40} width={40} />
                </div>
                <div className="px-4">
                  <div
                    aria-label={t('components.profile.email.ariaLabel')}
                    className="mb-1 overflow-hidden font-medium text-ellipsis whitespace-nowrap text-body2 text-neutral-90 dark:text-neutral-40"
                  >
                    {user.email}
                  </div>
                  {showManageAccountLink &&
                    <HrefLink
                      className="font-medium text-consumer-blue dark:text-dark-consumer-blue"
                      aria-label={t('components.profile.buttons.manageAccount.ariaLabel')}
                      label={t('components.profile.buttons.manageAccount.label')}
                      href={user.profile}
                      target="_blank"
                      isExternal={true}
                    />
                  }
                </div>
              </div>}
              <div className="p-6 border-b border-neutral-30 dark:border-neutral-80">
                <div className="flex items-center gap-2 pl-4 mb-5 font-medium text-body2 text-neutral-90 dark:text-neutral-20">
                  {isDark() ? <IconMoon /> : <IconSun />}
                  {t('components.ui.modeToggle.label')}
                </div>
                <RadioGroup className="flex flex-col">
                  <Radio
                    value={MODE.LIGHT}
                    checked={!isDark() && !isSystem()}
                    onClick={() => toggleDarkMode(MODE.LIGHT)}
                    image={lightMode}
                    aria-label={t('components.ui.modeToggle.light')}
                    label={t('components.ui.modeToggle.light')}
                    size="s"
                    containerClassName={clsx(
                      'py-2 px-4 border border-transparent',
                      !isDark() && !isSystem() && 'rounded border border-accent-30 bg-accent-10',
                    )}
                    labelClassName={clsx(!isDark() && !isSystem() && 'text-neutral-98')}
                    isMobile={true}
                  />
                  <Radio
                    value={MODE.DARK}
                    checked={isDark() && !isSystem()}
                    onClick={() => toggleDarkMode(MODE.DARK)}
                    image={darkMode}
                    aria-label={t('components.ui.modeToggle.dark')}
                    label={t('components.ui.modeToggle.dark')}
                    size="s"
                    containerClassName={clsx(
                      'py-2 px-4 border border-transparent',
                      isDark() && !isSystem() && 'rounded border border-neutral-80 bg-neutral-95',
                    )}
                    labelClassName={clsx(isDark() && !isSystem() && 'dark:text-neutral-20')}
                    isMobile={true}
                  />
                  <Radio
                    value={MODE.SYSTEM}
                    checked={isSystem()}
                    onClick={() => toggleDarkMode(MODE.SYSTEM)}
                    image={systemMode}
                    aria-label={t('components.ui.modeToggle.system')}
                    label={t('components.ui.modeToggle.system')}
                    size="s"
                    containerClassName={clsx(
                      'py-2 px-4 border border-transparent',
                      isSystem() && 'rounded border border-accent-30 dark:border-neutral-80 bg-accent-10 dark:bg-neutral-95',
                    )}
                    labelClassName={clsx(isSystem() && 'text-neutral-98 dark:text-neutral-20')}
                    isMobile={true}
                  />
                </RadioGroup>
              </div>
              {user?.email ?
                <Form schema={z.object({})} method="get" action="/services/api/logout">
                  {() => (
                    <div
                      aria-label={t('components.profile.buttons.logout.ariaLabel')}
                      className="relative flex items-center w-full p-6 text-neutral-90 dark:text-neutral-20"
                    >
                      <input
                        type="submit"
                        name="submit"
                        disabled={fetcher.state === 'submitting' || fetcher.state === 'loading'}
                        className="absolute inset-0 w-full h-full bg-transparent cursor-pointer"
                        value=""
                      />
                      <div className="py-2 pl-4 flex-0">
                        <IconSignOut />
                      </div>
                      <div className="flex-1 pl-2 overflow-hidden font-medium text-body2">
                        {t('components.profile.buttons.logout.label')}
                      </div>
                    </div>
                  )}
                </Form> :
                <div className="p-8">
                  <LinkButton
                    to="/services/api/login"
                    type="secondary"
                    data-testid="login-button"
                    size="m"
                    className="w-full justify-center text-neutral-98 dark:text-neutral-20 border-[1.5px]"
                    style={{ borderColor: accentColor, fontFamily }}
                  >
                    {t('components.ui.buttons.logIn.label')}
                  </LinkButton>
                </div>}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}